import { mimeTypes } from '@/shared/constants';
import { type DriveFile, type DriveMimeType } from '@/shared/DriveTypes';
import { create } from 'zustand';
import { DEV, hasWindow, isBun, LEGACY } from './constants';
import { log } from './logger';
import type { FileModalType, ModalType, NonEmptyString } from './types';

// Theming
type Theme = 'dark' | 'light';
type SetThemeAction = Theme | ((prevTheme: Theme) => Theme);
type ThemeState = {
  theme: Theme;
  setTheme: (action: SetThemeAction) => void;
};

const theme = isBun
  ? 'light'
  : ((localStorage.getItem('theme') as Theme | null) ??
    (matchMedia('(prefers-color-scheme:dark)').matches ? 'dark' : 'light'));

export const modalTypes = {
  document: mimeTypes.document,
  sheet: mimeTypes.spreadsheet,
  slide: mimeTypes.presentation,
  form: mimeTypes.form,
  folder: mimeTypes.folder,
  link: mimeTypes.document,
} satisfies Record<FileModalType, DriveMimeType>;

export const fileModalTypes = ['document', 'sheet', 'slide', 'form', 'folder'] satisfies FileModalType[];

type Status = 'idle' | 'loading' | 'error';

type MobileState = {
  isMobile: boolean;
  isTablet: boolean;
  sidebarOpen: boolean;
  actionsDrawerOpen: boolean;
  mobileSearchOpen: boolean;
  hasAutoFocusedSearch: boolean;
  editing: boolean;
  setSidebarOpen: (open: boolean) => void;
  setActionsDrawerOpen: (open: boolean) => void;
  setMobileSearchOpen: (open: boolean) => void;
  setHasAutoFocusedSearch: (value: boolean) => void;
  setEditing: (editing: boolean) => void;
};

export type Store = ThemeState &
  MobileState & {
    openId: string | undefined;
    setOpenId: (id: string | undefined, menuType?: 'tree') => void;
    lastPath: string | undefined;
    setLastPath: (path: string | undefined) => void;
    streamed: DriveFile[];
    streamedLength: number;
    setStreamed: (data: DriveFile[]) => void;
    toggleTheme: () => void;
    menuType: 'tree' | undefined;
    setMenuType: (type: 'tree' | undefined) => void;
    modalId: string | undefined;
    setModalId: (id: string | undefined) => void;
    modalType: ModalType | undefined;
    setModalType: (modal: ModalType | undefined) => void;
    newFile: DriveFile | null;
    setNewFile: (newFile: DriveFile | null) => void;
    toast:
      | {
          type?: 'error';
          message: string;
          action?: () => void;
          actionLabel?: string;
          autoHideDuration?: number | null;
        }
      | undefined;
    setToast: (toast: Store['toast'] | string) => void;
    status: Status;
    statusIds: Set<string>;
    setStatus: (status: Status, options?: { message?: string; id?: string }) => void;
    message: string | undefined;
  };

export const useStore = create<Store>()((set) => ({
  theme,
  isMobile: hasWindow ? window.innerWidth < 640 : false,
  isTablet: hasWindow ? window.innerWidth < 768 : false,
  sidebarOpen: false,
  actionsDrawerOpen: false,
  mobileSearchOpen: false,
  hasAutoFocusedSearch: false,
  editing: false,
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setActionsDrawerOpen: (actionsDrawerOpen) => set({ actionsDrawerOpen }),
  setMobileSearchOpen: (mobileSearchOpen) => set({ mobileSearchOpen }),
  setHasAutoFocusedSearch: (hasAutoFocusedSearch) => set({ hasAutoFocusedSearch }),
  setEditing: (editing) => set({ editing }),
  streamed: [],
  openId: undefined,
  lastPath: undefined,
  setLastPath: (lastPath) => set({ lastPath }),
  setOpenId: (openId, menuType) => set({ openId, menuType }),
  menuType: undefined,
  setMenuType: (menuType) => set({ menuType }),
  modalId: '',
  setModalId: (modalId) => set({ modalId }),
  modalType: undefined,
  newFile: null,
  streamedLength: 0,
  status: 'idle',
  statusIds: new Set(),
  message: undefined,
  toast: undefined,
  setNewFile: (newFile) => set({ newFile }),
  setModalType: (modalType) => set({ modalType }),
  setStreamed: (data) => set({ streamed: data }),
  setTheme: (action) => {
    set((state) => {
      const newTheme = typeof action === 'function' ? action(state.theme) : action;
      document.documentElement.setAttribute('data-theme', newTheme);
      document.documentElement.style.setProperty('--root-color-scheme', newTheme);
      localStorage.setItem('theme', newTheme);
      return { theme: newTheme };
    });
  },
  toggleTheme: () => {
    set((state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', newTheme);
      document.documentElement.style.setProperty('--root-color-scheme', newTheme);
      localStorage.setItem('theme', newTheme);
      return { theme: newTheme };
    });
  },
  setToast: (toast) => (typeof toast === 'string' ? set({ toast: { message: toast } }) : set({ toast })),
  setStatus: (status, options) =>
    set((state) => {
      if (!options?.id) {
        return { status: state.statusIds.size > 0 ? 'loading' : status, message: options?.message };
      }

      if (DEV && status === 'idle' && options.id && state.statusIds.size > 0 && !state.statusIds.has(options.id)) {
        log.error(
          `set status 'idle' for id ${options.id} which is not in statusIds, ${Array.from(state.statusIds).join(', ')}`
        );
      }

      const nextStatusIds = new Set(state.statusIds);
      status === 'loading' ? nextStatusIds.add(options.id) : nextStatusIds.delete(options.id);

      return {
        status: nextStatusIds.size > 0 ? 'loading' : 'idle',
        statusIds: nextStatusIds,
        message: nextStatusIds.size > 0 ? state.message : undefined,
      };
    }),
}));

if (hasWindow) {
  window.matchMedia('(max-width: 639px)').addEventListener('change', (e) => {
    useStore.setState({ isMobile: e.matches });
  });
  window.matchMedia('(max-width: 767px)').addEventListener('change', (e) => {
    useStore.setState({ isTablet: e.matches });
  });
}

export const useOpenId = (type?: Store['menuType']) => {
  const openId = useStore((state) => state.openId);
  const set = useStore((state) => state.setOpenId);
  const menuType = useStore((state) => state.menuType);
  const setMenuType = useStore((state) => state.setMenuType);
  const setOpenId = <T extends string>(id: NonEmptyString<T> | undefined, delay?: number) => {
    if (isBun) {
      set(id);
      setMenuType(type);
      setTimeout(() => {
        set(id);
        setMenuType(type);
      });
    } else if (delay === undefined) {
      set(id);
      setMenuType(type);
    } else {
      setTimeout(() => {
        set(id);
        setMenuType(type);
      }, delay);
    }
  };
  return {
    openId: menuType === type ? openId : undefined,
    setOpenId,
    menuType,
  } as const;
};

export const useModalId = () => {
  const modalId = useStore((state) => state.modalId);
  const setModalId = useStore((state) => state.setModalId);
  return [modalId, setModalId] as const;
};

export const useModal = (openId: string | undefined, options?: { onOpen?: () => void; onClose?: () => void }) => {
  const modalId = useStore((state) => state.modalId);
  const modalType = useStore((state) => state.modalType);
  const setModalId = useStore((state) => state.setModalId);
  const setModalType = useStore((state) => state.setModalType);
  const openModal = (type: ModalType) => {
    setModalId(openId);
    setModalType(type);
    options?.onOpen?.();
  };
  const closeModal = () => {
    setModalId(undefined);
    setModalType(undefined);
    options?.onClose?.();
  };

  return { modalId, modalType, openModal, closeModal, setModalId };
};

export const useTheme = () => useStore((state) => (LEGACY ? 'light' : state.theme));
export const useSetTheme = () => useStore((state) => state.setTheme);
export const useToggleTheme = () => useStore((state) => state.toggleTheme);

export const useStreamed = () => {
  const streamed = useStore((state) => state.streamed);
  const setStreamed = useStore((state) => state.setStreamed);
  // the streaming array uses the same reference
  // which will not trigger an update
  // so we update on changes to length
  useStore((state) => state.streamed.length);
  return [streamed, setStreamed] as const;
};

export const useMobile = () => useStore((state) => state.isMobile);
export const useTablet = () => useStore((state) => state.isTablet);
export const useSidebarOpen = () => {
  const sidebarOpen = useStore((state) => state.sidebarOpen);
  const setSidebarOpen = useStore((state) => state.setSidebarOpen);
  return [sidebarOpen, setSidebarOpen] as const;
};
export const useActionsDrawerOpen = () => {
  const actionsDrawerOpen = useStore((state) => state.actionsDrawerOpen);
  const setActionsDrawerOpen = useStore((state) => state.setActionsDrawerOpen);
  return [actionsDrawerOpen, setActionsDrawerOpen] as const;
};
export const useMobileSearchOpen = () => {
  const mobileSearchOpen = useStore((state) => state.mobileSearchOpen);
  const setMobileSearchOpen = useStore((state) => state.setMobileSearchOpen);
  return [mobileSearchOpen, setMobileSearchOpen] as const;
};
